// Physios.jsx
import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PageHeader, Card, Tag } from "../components/UI";
import DataTable from "../components/DataTable";
import { MessageCircle, Sparkles, Plus, X, Wallet, Info, ShieldCheck, Users, Award, AlertCircle, Trash2, Ban, ShieldOff } from "lucide-react";
import { TimeFilter, periodRange } from "../components/TimeFilter";
import { useAuth } from "../context/AuthContext";
import api, { formatError } from "../lib/api";

// Module-level helper to keep the useEffect body small (the lint rule mis-fires on inline async fetches).
async function fetchPhysioDetail(id, signal, setters) {
  try {
    const { data } = await api.get(`/physios/${id}`, { signal });
    if (signal.aborted) return;
    setters.setDetail(data);
    setters.setTab("profile");
    setters.setEditForm({
      specialty: data.physio.specialty || "",
      preferred_annual_reward: data.physio.preferred_annual_reward || "",
      preferred_quarterly_reward: data.physio.preferred_quarterly_reward || "",
      address: data.physio.address || "",
      clinic_name: data.physio.clinic_name || "",
      city: data.physio.city || "",
      state: data.physio.state || "",
    });
    try { const eg = await api.get(`/physios/${id}/engagement-score`, { signal }); if (!signal.aborted) setters.setEngagement(eg.data); } catch { if (!signal.aborted) setters.setEngagement(null); }
    try { const rf = await api.get(`/physios/${id}/referrals`, { signal }); if (!signal.aborted) setters.setReferralData(rf.data); } catch { if (!signal.aborted) setters.setReferralData(null); }
    try { const nm = await api.get(`/physios/${id}/next-milestone`, { signal }); if (!signal.aborted) setters.setNextMilestone(nm.data); } catch { if (!signal.aborted) setters.setNextMilestone(null); }
  } catch {
    // Aborted or network error — swallow
  }
}
import { fmt } from "../components/UI";

export default function Physios() {
  const { user } = useAuth();
  const isDse = user?.role === "dse";
  const [items, setItems] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [dseList, setDseList] = useState([]);
  const [dseError, setDseError] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("profile"); // profile / dpdp / referrals
  const [engagement, setEngagement] = useState(null);
  const [referralData, setReferralData] = useState(null);
  const [nextMilestone, setNextMilestone] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ specialty: "", preferred_annual_reward: "", preferred_quarterly_reward: "", address: "", clinic_name: "", city: "", state: "" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ mobile: "", first_name: "", last_name: "", city: "", state: "", region: "", specialty: "Sports Physiotherapy", clinic_name: "", preferred_annual_reward: "", preferred_quarterly_reward: "", erp_outlet_id: "", assigned_dse: "", tnc_accepted: true, opt_in_email: false, opt_in_sms: false });
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [preset, setPreset] = useState("this_month");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  // Point 40 — server-side search so ALL physios are searchable by name / mobile,
  // not just the rows currently loaded on the client.
  const [serverSearch, setServerSearch] = useState("");
  const handleServerSearch = useCallback((term) => {
    setServerSearch(term || "");
  }, []);

  async function fetchAll() {
    const { start, end } = periodRange(preset, customStart, customEnd);
    const qs = start && end ? `&start_date=${start}&end_date=${end}` : "";
    const [a, b] = await Promise.all([
      api.get(`/physios?limit=500${qs}`),
      api.get("/config/specialties"),
    ]);
    setItems(a.data); setSpecialties(b.data);
    try {
      const u = await api.get("/users?role=dse");
      setDseList(u.data || []);
      setDseError(false);
    } catch {
      setDseList([]); setDseError(true);
    }
  }
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const b = await api.get("/config/specialties");
        if (alive) setSpecialties(b.data);
      } catch { /* swallow */ }
      try {
        const u = await api.get("/users?role=dse");
        if (alive) { setDseList(u.data || []); setDseError(false); }
      } catch {
        if (alive) { setDseList([]); setDseError(true); }
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    const { start, end } = periodRange(preset, customStart, customEnd);
    const qs = start && end ? `&start_date=${start}&end_date=${end}` : "";
    const term = serverSearch.trim();
    // When searching, query the whole master (higher limit); otherwise browse the recent set.
    const searchQs = term ? `&search=${encodeURIComponent(term)}&limit=1000` : "&limit=500";
    (async () => {
      try {
        const a = await api.get(`/physios?${searchQs.slice(1)}${qs}`);
        if (alive) setItems(a.data);
      } catch { /* swallow */ }
    })();
    return () => { alive = false; };
  }, [preset, customStart, customEnd, serverSearch]);

  useEffect(() => {
    if (!selected) {
      setDetail(null); setEngagement(null); setReferralData(null); setNextMilestone(null); setEditing(false);
      return undefined;
    }
    const controller = new AbortController();
    fetchPhysioDetail(selected, controller.signal, {
      setDetail, setTab, setEditForm, setEngagement, setReferralData, setNextMilestone,
    });
    return () => controller.abort();
  }, [selected]);

  async function saveEdit() {
    try {
      await api.patch(`/physios/${selected}`, editForm);
      const { data } = await api.get(`/physios/${selected}`);
      setDetail(data); setEditing(false); await fetchAll();
    } catch (e) { alert(formatError(e)); }
  }

  async function submit(e) {
    e.preventDefault();
    try { await api.post("/physios", form); setOpen(false); await fetchAll(); }
    catch (e) { alert(formatError(e)); }
  }

  async function deletePhysio(id, e) {
    if (e) e.stopPropagation();
    const p = items.find((x) => x.id === id);
    const who = p ? `${p.first_name} ${p.last_name} (${p.erp_outlet_id || p.mobile || ""})` : "this physio";
    if (!window.confirm(`Delete ${who}? This removes their wallet, points and transaction history. This cannot be undone.`)) return;
    try {
      await api.delete(`/physios/${id}`);
      if (selected === id) setSelected(null);
      await fetchAll();
    } catch (err) { alert(formatError(err)); }
  }

  async function toggleBlock(id, e) {
    if (e) e.stopPropagation();
    const p = items.find((x) => x.id === id) || (detail?.physio?.id === id ? detail.physio : null);
    const currentlyBlocked = !!p?.blocked;
    const who = p ? `${p.first_name} ${p.last_name} (${p.erp_outlet_id || p.mobile || ""})` : "this physio";
    let reason = null;
    if (!currentlyBlocked) {
      reason = window.prompt(`Block ${who}? They will not be able to redeem points via WhatsApp until unblocked.\n\nOptional reason:`, "");
      if (reason === null) return; // cancelled
    } else if (!window.confirm(`Unblock ${who}? They will be able to redeem points again.`)) {
      return;
    }
    try {
      await api.post(`/physios/${id}/block`, { blocked: !currentlyBlocked, reason: reason || null });
      // Optimistic local update — keeps the current search/filter view intact and shows
      // the Blocked tag immediately without a full refetch.
      setItems((prev) => prev.map((x) => (x.id === id ? { ...x, blocked: !currentlyBlocked } : x)));
      if (selected === id) { const { data } = await api.get(`/physios/${id}`); setDetail(data); }
    } catch (err) { alert(formatError(err)); }
  }

  async function toggleOptIn(field, val) {
    try {
      await api.patch(`/physios/${selected}/opt-ins`, { [field]: val });
      // Refresh detail
      const { data } = await api.get(`/physios/${selected}`);
      setDetail(data);
    } catch (e) { alert(formatError(e)); }
  }

  async function addSpecialtyInline() {
    const name = window.prompt("New specialty (e.g. Ortho MD, Pain Specialist)");
    if (!name) return;
    try {
      await api.post("/config/specialties", { name });
      const { data } = await api.get("/config/specialties");
      setSpecialties(data);
    } catch (e) { alert(formatError(e)); }
  }

  // Build dynamic options from data
  const states = [...new Set(items.map((p) => p.state).filter(Boolean))].sort();
  const regions = [...new Set(items.map((p) => p.region).filter(Boolean))].sort();
  const cities = [...new Set(items.map((p) => p.city).filter(Boolean))].sort();
  const kycs = [...new Set(items.map((p) => p.kyc_status).filter(Boolean))];
  const specialtyOptions = specialties.map((s) => s.name);
  const milestoneOptions = [...new Set(items.map((p) => p.next_milestone?.name).filter(Boolean))].sort();
  const dseById = Object.fromEntries(dseList.map((d) => [d.id, d.name || d.erp_id || d.email]));
  const dseByErp = Object.fromEntries(dseList.filter((d) => d.erp_id).map((d) => [d.erp_id.toUpperCase(), d.name || d.erp_id || d.email]));
  // Resolve DSE display for a physio row (prefer ERP linkage from import, fall back to assigned_dse id)
  const physioDseName = (p) => {
    if (p.assigned_dse && dseById[p.assigned_dse]) return dseById[p.assigned_dse];
    if (p.assigned_dse_erp && dseByErp[String(p.assigned_dse_erp).toUpperCase()]) return dseByErp[String(p.assigned_dse_erp).toUpperCase()];
    if (p.l1_user_name) return p.l1_user_name;
    if (p.assigned_dse_erp) return p.assigned_dse_erp;
    return "";
  };
  const dseOptions = [...new Set(items.map(physioDseName).filter(Boolean))].sort();

  // Apply query-string preset
  const initialStateFilter = sp.get("state");
  const { start: pStart, end: pEnd } = periodRange(preset, customStart, customEnd);
  const periodActive = !!(pStart && pEnd);

  const columns = [
    { key: "name", label: "Physio", accessor: (r) => `${r.first_name} ${r.last_name}`, filter: "text",
      render: (r) => (
        <div className="font-medium text-slate-900 flex items-center gap-2">
          <span>{r.first_name} {r.last_name}</span>
          {r.blocked && <Tag tone="rose" data-testid={`physio-blocked-tag-${r.id}`}>Blocked</Tag>}
        </div>
      ) },
    { key: "outlet_erp_id", label: "ERP ID", accessor: (r) => r.erp_outlet_id || "", filter: "text",
      render: (r) => <span className="font-mono text-[12px] text-slate-700">{r.erp_outlet_id || "—"}</span> },
    { key: "mobile", label: "Mobile", filter: "text", render: (r) => <span className="font-mono text-[12px]">{r.mobile}</span> },
    { key: "clinic_name", label: "Clinic", filter: "text",
      render: (r) => r.clinic_name ? <span className="text-[12px] text-slate-700">{r.clinic_name}</span> : <span className="text-slate-400 italic text-[11px]">—</span> },
    { key: "city", label: "City", filter: "select", options: cities },
    { key: "state", label: "State", filter: "select", options: states },
    { key: "region", label: "Region", filter: "select", options: regions,
      render: (r) => r.region ? <span className="text-[12px] text-slate-700">{r.region}</span> : <span className="text-slate-400 italic text-[11px]">—</span> },
    { key: "dse", label: "DSE", accessor: physioDseName, filter: "select", options: dseOptions,
      render: (r) => {
        const n = physioDseName(r);
        return n ? <span className="text-[12px] text-slate-700">{n}</span> : <span className="text-slate-400 italic text-[11px]">—</span>;
      } },
    { key: "q1_points", label: "Q1 Points", accessor: (r) => r.q1_points ?? 0, filter: "range", align: "right",
      render: (r) => (
        <div className="flex items-center justify-end min-w-[64px]" data-testid={`q1-points-${r.id}`}>
          <span className="text-[11px] numerals text-slate-700">{fmt(r.q1_points || 0)}</span>
        </div>
      ) },
    { key: "q1_balance_points", label: "Q1 Balance Point", accessor: (r) => r.q1_balance_points ?? 0, filter: "range", align: "right",
      render: (r) => (
        <div className="flex items-center justify-end min-w-[64px]" data-testid={`q1-balance-${r.id}`}>
          <span className="text-[11px] numerals text-emerald-700">{fmt(r.q1_balance_points || 0)}</span>
        </div>
      ) },
    { key: "q2_points", label: "Q2 Points", accessor: (r) => r.q2_points ?? 0, filter: "range", align: "right",
      render: (r) => (
        <div className="flex items-center justify-end min-w-[64px]" data-testid={`q2-points-${r.id}`}>
          <span className="text-[11px] numerals text-slate-700">{fmt(r.q2_points || 0)}</span>
        </div>
      ) },
    { key: "annual_points", label: "Annual Points", accessor: (r) => r.annual_points ?? 0, filter: "range", align: "right",
      render: (r) => (
        <div className="flex items-center justify-end min-w-[64px]" data-testid={`annual-points-${r.id}`}>
          <span className="text-[11px] numerals text-slate-700">{fmt(r.annual_points || 0)}</span>
        </div>
      ) },
    ...(periodActive ? [{ key: "period_points", label: "Points (Period)", accessor: (r) => r.period_points ?? 0, filter: "range", align: "right",
      render: (r) => (
        <div className="flex items-center justify-end min-w-[72px]" data-testid={`period-points-${r.id}`}>
          <span className="text-[11px] numerals text-teal-700 font-medium">{fmt(r.period_points || 0)}</span>
        </div>
      ) }] : []),
    { key: "next_milestone", label: "Next milestone", accessor: (r) => r.next_milestone?.name || "—", filter: "select", options: milestoneOptions,
      render: (r) => r.next_milestone ? (
        <div className="text-[11.5px]" data-testid={`next-ms-${r.id}`}>
          <div className="font-medium text-slate-800">{r.next_milestone.name}</div>
          <div className="text-amber-700 numerals">{fmt(r.next_milestone.points_needed)} pts to go</div>
        </div>
      ) : <span className="text-emerald-700 text-[11px]">All unlocked</span> },
    { key: "preferred_quarterly_reward", label: "Pref · Qtr Reward", filter: "text",
      render: (r) => <span className="text-[12px] text-slate-700">{r.preferred_quarterly_reward || <span className="text-slate-400 italic">—</span>}</span> },
    { key: "preferred_annual_reward", label: "Pref · Annual Reward", filter: "text",
      render: (r) => <span className="text-[12px] text-slate-700">{r.preferred_annual_reward || <span className="text-slate-400 italic">—</span>}</span> },
    { key: "certified", label: "Certified", filter: "select", options: ["Yes", "No"],
      accessor: (r) => r.certified ? "Yes" : "No",
      render: (r) => r.certified ? <Tag tone="emerald">Yes</Tag> : <Tag tone="slate">No</Tag> },
    { key: "last_activity_at", label: "Last activity", filter: "text",
      render: (r) => r.last_activity_at ? new Date(r.last_activity_at).toLocaleDateString() : "—" },
    { key: "actions", label: "", sortable: false, align: "right",
      render: (r) => (
        <div className="inline-flex items-center gap-2">
          <button onClick={(e) => toggleBlock(r.id, e)} className={r.blocked ? "text-amber-600 hover:text-emerald-600" : "text-slate-400 hover:text-amber-600"} title={r.blocked ? "Unblock physio" : "Block physio"} data-testid={`block-physio-${r.id}`}>
            {r.blocked ? <ShieldOff size={14} /> : <Ban size={14} />}
          </button>
          <button onClick={(e) => deletePhysio(r.id, e)} className="text-slate-400 hover:text-rose-600" title="Delete physio" data-testid={`delete-physio-${r.id}`}><Trash2 size={14} /></button>
        </div>
      ) },
  ];

  // Point 30 — DSE panel is read-only: hide City/State/Region/DSE + delete-action columns.
  const hiddenForDse = new Set(["city", "state", "region", "dse", "actions"]);
  const visibleColumns = isDse ? columns.filter((c) => !hiddenForDse.has(c.key)) : columns;

  return (
    <div className="space-y-6">
      <PageHeader
        kicker="Network"
        title="Physio Master"
        subtitle="The single source of truth for every physiotherapist enrolled in the CBC program — sortable, filterable, drill-through to the full profile with engagement score breakdown, DPDP opt-ins and referral tree."
        testId="physios-page"
        actions={!isDse && <button onClick={() => setOpen(true)} className="btn-primary flex items-center gap-2" data-testid="add-physio-btn"><Plus size={14} /> Onboard physio</button>}
      />

      <TimeFilter preset={preset} setPreset={setPreset} customStart={customStart} setCustomStart={setCustomStart} customEnd={customEnd} setCustomEnd={setCustomEnd} testId="physios-time-filter" />

      <Card>
        <DataTable
          columns={visibleColumns}
          data={items}
          rowKey="id"
          onRowClick={(r) => setSelected(r.id)}
          searchableKeys={["first_name", "last_name", "mobile", "clinic_name", "erp_outlet_id", "city", "state"]}
          onServerSearch={handleServerSearch}
          serverSearchPlaceholder="Search all physios by name / mobile…"
          exportFilename="cbc-physios"
          testIdPrefix="physios"
          initialSort={{ key: "engagement", dir: "desc" }}
          paginated
          pageSize={25}
        />
        {initialStateFilter && <div className="text-[11px] text-slate-500 mt-2">Pre-filtered from dashboard: state = {initialStateFilter}.</div>}
      </Card>

      {/* Detail drawer */}
      {selected && detail && (
        <div className="fixed inset-0 bg-slate-900/30 z-40" onClick={() => setSelected(null)}>
          <div className="absolute right-0 top-0 h-full w-full md:w-[640px] bg-white border-l border-slate-200 overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="physio-drawer">
            <div className="px-6 py-5 border-b border-slate-100 flex items-start justify-between sticky top-0 bg-white z-10">
              <div>
                <div className="small-caps text-teal-700">Physio Profile</div>
                <div className="serif-display text-2xl mt-1 flex items-center gap-2">
                  {detail.physio.first_name} {detail.physio.last_name}
                  {detail.physio.blocked && <Tag tone="rose" data-testid="drawer-blocked-tag">Blocked</Tag>}
                </div>
                <div className="text-[12px] text-slate-500 mt-1 flex items-center gap-2">
                  <span className="font-mono">{detail.physio.erp_outlet_id || "—"}</span>
                  <span>·</span>
                  <span>{detail.physio.clinic_name}</span>
                  <span>·</span>
                  <span>{detail.physio.city}, {detail.physio.state}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!isDse && (
                  <button onClick={() => toggleBlock(detail.physio.id)}
                    className={`text-[12px] px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5 transition ${detail.physio.blocked ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border-rose-300 text-rose-700 hover:bg-rose-50"}`}
                    data-testid="drawer-block-toggle">
                    {detail.physio.blocked ? <><ShieldOff size={13} /> Unblock</> : <><Ban size={13} /> Block</>}
                  </button>
                )}
                <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-900"><X size={18} /></button>
              </div>
            </div>

            {/* Tabs */}
            <div className="px-6 pt-3 border-b border-slate-100 flex gap-4 text-[12.5px]">
              {[
                ["profile", "Profile"],
                ["dpdp", "DPDP & Consent"],
                ["referrals", `Referrals ${referralData?.count ? `(${referralData.count})` : ""}`],
              ].map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
                  className={`pb-2 -mb-[1px] border-b-2 transition ${tab === k ? "border-teal-700 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-900"}`}>{l}</button>
              ))}
            </div>

            <div className="p-6 space-y-5">
              {tab === "profile" && (
                <>
                  <div className="flex items-center justify-between">
                    <div className="small-caps text-slate-500">Profile details</div>
                    {!isDse && (
                      <button onClick={() => setEditing(!editing)} className="text-[12px] text-teal-700 hover:underline inline-flex items-center gap-1" data-testid="edit-physio-btn">
                        {editing ? "Cancel" : "✏️ Edit"}
                      </button>
                    )}
                  </div>
                  {editing ? (
                    <div className="surface p-3 space-y-2" data-testid="edit-physio-panel">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="small-caps text-slate-500">Specialty</label>
                          <select value={editForm.specialty} onChange={(e) => setEditForm({ ...editForm, specialty: e.target.value })} data-testid="edit-specialty">
                            <option value="">— Select —</option>
                            {specialtyOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="small-caps text-slate-500">Preferred Annual Reward</label>
                          <input value={editForm.preferred_annual_reward} onChange={(e) => setEditForm({ ...editForm, preferred_annual_reward: e.target.value })} placeholder="e.g. International family vacation" data-testid="edit-pref-annual" />
                        </div>
                        <div>
                          <label className="small-caps text-slate-500">Preferred Quarterly Reward</label>
                          <input value={editForm.preferred_quarterly_reward} onChange={(e) => setEditForm({ ...editForm, preferred_quarterly_reward: e.target.value })} placeholder="e.g. Smartwatch" data-testid="edit-pref-quarterly" />
                        </div>
                        <div className="col-span-2">
                          <label className="small-caps text-slate-500">Clinic Name</label>
                          <input value={editForm.clinic_name} onChange={(e) => setEditForm({ ...editForm, clinic_name: e.target.value })} />
                        </div>
                        <div className="col-span-2">
                          <label className="small-caps text-slate-500">Address</label>
                          <textarea rows={2} value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} className="w-full" />
                        </div>
                        <div><label className="small-caps text-slate-500">City</label><input value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} /></div>
                        <div><label className="small-caps text-slate-500">State</label><input value={editForm.state} onChange={(e) => setEditForm({ ...editForm, state: e.target.value })} /></div>
                      </div>
                      <button onClick={saveEdit} className="btn-primary w-full !py-1.5" data-testid="save-edit-btn">Save changes</button>
                    </div>
                  ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="surface p-3"><div className="small-caps text-slate-400">ERP ID</div><div className="font-mono text-[13px] mt-1">{detail.physio.erp_outlet_id || "—"}</div></div>
                    <div className="surface p-3"><div className="small-caps text-slate-400">Mobile</div><div className="font-mono text-[13px] mt-1">{detail.physio.mobile}</div></div>
                    <div className="surface p-3"><div className="small-caps text-slate-400">Reception #</div><div className="font-mono text-[13px] mt-1">{detail.physio.reception_number || "—"}</div></div>
                    <div className="surface p-3"><div className="small-caps text-slate-400">Reg #</div><div className="font-mono text-[13px] mt-1">{detail.physio.registration_number || "—"}</div></div>
                    <div className="surface p-3"><div className="small-caps text-slate-400">Qualification</div><div className="text-[13px] mt-1">{detail.physio.qualification || "—"}</div></div>
                    <div className="surface p-3"><div className="small-caps text-slate-400">Specialty</div><div className="text-[13px] mt-1">{detail.physio.specialty || "—"}</div></div>
                    <div className="surface p-3"><div className="small-caps text-slate-400">Source</div><div className="text-[13px] mt-1">{detail.physio.source_of_enrollment || detail.physio.referral_source || "—"}</div></div>
                    <div className="surface p-3"><div className="small-caps text-slate-400">Language</div><div className="text-[13px] mt-1">{detail.physio.preferred_language?.toUpperCase()}</div></div>
                    <div className="surface p-3 col-span-2"><div className="small-caps text-slate-400">Preferred Annual Reward (CBC target)</div><div className="text-[13px] mt-1">{detail.physio.preferred_annual_reward || <span className="text-slate-400 italic">Not captured — click Edit to set</span>}</div></div>
                    <div className="surface p-3 col-span-2"><div className="small-caps text-slate-400">Preferred Quarterly Reward</div><div className="text-[13px] mt-1">{detail.physio.preferred_quarterly_reward || <span className="text-slate-400 italic">Not captured — click Edit to set</span>}</div></div>
                    <div className="surface p-3 col-span-2"><div className="small-caps text-slate-400">Address</div><div className="text-[13px] mt-1">{detail.physio.address || "—"}</div></div>
                  </div>
                  )}

                  {/* Next Milestone Progress */}
                  {nextMilestone && (
                    <div className="surface p-4" data-testid="next-milestone-card">
                      <div className="flex items-center gap-2 mb-3"><Award size={14} className="text-amber-600" /><div className="font-medium text-[13px]">Next Milestone</div></div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {["quarterly", "annual"].map((w) => {
                          const m = nextMilestone[w];
                          return (
                            <div key={w} className="surface p-3 bg-slate-50">
                              <div className="small-caps text-slate-400 capitalize">{w}</div>
                              {m ? (
                                <>
                                  <div className="text-[13px] font-medium mt-1">{m.name}</div>
                                  <div className="text-[11px] text-slate-500">{m.reward_label} · +{fmt(m.bonus_points)} bonus</div>
                                  <div className="mt-2 h-1.5 bg-white rounded overflow-hidden">
                                    <div className="h-full bg-teal-600" style={{ width: `${m.progress_pct}%` }} />
                                  </div>
                                  <div className="flex justify-between text-[10.5px] mt-1 numerals">
                                    <span>{fmt(m.current_points)} / {fmt(m.threshold)}</span>
                                    <span className="text-teal-700 font-medium">{m.progress_pct}%</span>
                                  </div>
                                  <div className="text-[11px] text-amber-700 mt-1">{fmt(m.points_to_unlock)} pts to unlock</div>
                                </>
                              ) : (
                                <div className="text-[12px] text-emerald-700 mt-2">🏆 All {w} slabs unlocked!</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Engagement Score with drill-down */}
                  <div className="surface p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Sparkles size={14} className="text-teal-700" />
                        <div className="font-medium text-[13px]">Engagement Score</div>
                        <span title={engagement ? `Weighted score across 5 components.\nRecency (${engagement.weights.recency}%) · Frequency (${engagement.weights.frequency}%) · Monetary (${engagement.weights.monetary}%) · WhatsApp (${engagement.weights.whatsapp}%) · Certifications (${engagement.weights.certifications}%)\nRefresh: live on request.\nConfigurable via Admin → Config → Engagement Weights.` : ""} className="text-slate-400 cursor-help">
                          <Info size={12} />
                        </span>
                      </div>
                      {engagement && (
                        <Tag tone={engagement.score > 75 ? "teal" : engagement.score > 50 ? "amber" : "rose"}>E {Math.round(engagement.score)}</Tag>
                      )}
                    </div>
                    {engagement ? (
                      <div className="space-y-2" data-testid="engagement-breakdown">
                        {Object.entries(engagement.components).map(([k, c]) => (
                          <div key={k} className="flex items-center gap-2">
                            <div className="w-24 text-[11px] capitalize text-slate-500">{k} <span className="text-slate-400">·{c.weight}%</span></div>
                            <div className="flex-1 h-2 bg-slate-100 rounded overflow-hidden">
                              <div className="h-full bg-teal-600" style={{ width: `${c.value}%` }} />
                            </div>
                            <div className="text-[11px] w-16 text-right font-mono">{c.value}</div>
                            <div className="text-[10.5px] text-slate-400 w-32 text-right">{c.raw}</div>
                          </div>
                        ))}
                      </div>
                    ) : <div className="text-[11px] text-slate-400">Computing…</div>}
                  </div>

                  <div className="ai-surface p-4">
                    <div className="flex items-center gap-2 mb-2 text-teal-900 text-[13px] font-medium"><Wallet size={14} /> Wallet</div>
                    <div className="grid grid-cols-4 gap-2 text-center">
                      <div><div className="serif-display text-2xl numerals">{fmt(detail.wallet?.pending)}</div><div className="small-caps text-teal-900/60">Pending</div></div>
                      <div><div className="serif-display text-2xl numerals">{fmt(detail.wallet?.active)}</div><div className="small-caps text-teal-900/60">Active</div></div>
                      <div><div className="serif-display text-2xl numerals text-amber-700">{fmt(detail.wallet?.reserved || 0)}</div><div className="small-caps text-teal-900/60">Reserved</div></div>
                      <div><div className="serif-display text-2xl numerals">{fmt(detail.wallet?.redeemed)}</div><div className="small-caps text-teal-900/60">Redeemed</div></div>
                    </div>
                    <button onClick={() => nav(`/wallets?physio=${detail.physio.id}`)} className="btn-teal w-full mt-3 !py-1.5">Open in Wallet Engine →</button>
                  </div>

                  <div>
                    <div className="small-caps text-slate-500 mb-2">Recent transactions</div>
                    <div className="space-y-1.5">
                      {detail.transactions.length ? detail.transactions.slice(0, 10).map((t) => (
                        <div key={t.id} className="flex items-center justify-between text-[12px] py-2 px-3 surface">
                          <div><div className="font-medium capitalize">{t.type.replace(/_/g, " ")}</div><div className="text-slate-500">{new Date(t.at).toLocaleDateString()} · {t.order_ref || t.linked_order || t.linked_redemption || "—"}</div></div>
                          <div className={`numerals font-medium ${t.points < 0 ? "text-rose-600" : ""}`}>{t.points > 0 ? "+" : ""}{t.points}</div>
                        </div>
                      )) : <div className="text-slate-400 text-[12px]">No transactions yet.</div>}
                    </div>
                  </div>
                  <button className="btn-teal w-full flex items-center justify-center gap-2"><MessageCircle size={14} /> Message via WhatsApp</button>
                </>
              )}

              {tab === "dpdp" && (
                <div className="space-y-3" data-testid="dpdp-tab">
                  <div className="surface p-4 border-l-4 border-teal-700">
                    <div className="flex items-center gap-2 mb-3"><ShieldCheck size={14} className="text-teal-700" /><div className="font-medium text-[13px]">DPDP Compliance & Consent</div></div>
                    <div className="text-[11.5px] text-slate-500 mb-3">Capture &amp; manage consent for T&amp;C, WhatsApp, Email, and SMS communications per India DPDP Act 2023. Physios with opt-outs are excluded from those campaign channels.</div>

                    {[
                      ["tnc_accepted", "Terms & Conditions", "tnc_accepted_at"],
                      ["opt_in_whatsapp", "WhatsApp Communications", "whatsapp_opt_in_at"],
                      ["opt_in_email", "Email Marketing", "opt_in_email_at"],
                      ["opt_in_sms", "SMS Notifications", "opt_in_sms_at"],
                    ].map(([field, label, tsField]) => {
                      const val = field === "opt_in_whatsapp" ? (detail.physio.opt_in_whatsapp ?? detail.physio.whatsapp_opt_in) : detail.physio[field];
                      const ts = detail.physio[tsField];
                      return (
                        <div key={field} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                          <div>
                            <div className="text-[13px] font-medium">{label}</div>
                            {ts && <div className="text-[10.5px] text-slate-400">Captured: {new Date(ts).toLocaleString()}</div>}
                          </div>
                          <label className="inline-flex items-center cursor-pointer">
                            <input type="checkbox" checked={!!val} onChange={(e) => toggleOptIn(field, e.target.checked)} data-testid={`optin-${field}`} className="sr-only peer" />
                            <div className="w-10 h-5 bg-slate-200 peer-checked:bg-teal-600 rounded-full transition relative">
                              <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition ${val ? "translate-x-5" : ""}`}></div>
                            </div>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {tab === "referrals" && (
                <div className="space-y-3" data-testid="referrals-tab">
                  <div className="surface p-4 border-l-4 border-teal-700">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="small-caps text-teal-700 flex items-center gap-1"><Users size={11} /> Give a referral</div>
                        <div className="text-[13px] mt-1">Referral code: <span className="font-mono font-medium" data-testid="physio-referral-code">{detail.physio.referral_code || "—"}</span></div>
                        <p className="text-[11.5px] text-slate-500 mt-1">Earn 500 bonus points on each qualified referral.</p>
                      </div>
                      <button onClick={() => nav(`/referrals?referrer=${detail.physio.id}`)} className="btn-teal !py-1 whitespace-nowrap" data-testid="give-referral-btn">Give Referral →</button>
                    </div>
                  </div>

                  {referralData ? (
                    <>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="surface p-3"><div className="small-caps text-slate-400">Total Referred</div><div className="serif-display text-2xl numerals">{referralData.count}</div></div>
                        <div className="surface p-3"><div className="small-caps text-slate-400">Bonus Points Earned</div><div className="serif-display text-2xl numerals text-teal-700">{fmt(referralData.total_bonus_points)}</div></div>
                      </div>
                      <div className="space-y-1.5">
                        {referralData.referrals.length ? referralData.referrals.map((r) => (
                          <div key={r.id} className="surface p-3 flex items-center justify-between" data-testid={`referral-row-${r.id}`}>
                            <div>
                              <div className="font-medium text-[13px]">{r.referred_name}</div>
                              <div className="text-[11px] text-slate-500">{r.referred_mobile} · {r.city} · {new Date(r.created_at).toLocaleDateString()}</div>
                            </div>
                            <div className="text-right">
                              <Tag tone={r.status === "qualified" ? "emerald" : r.status === "rejected" ? "rose" : "amber"}>{r.status}</Tag>
                              {r.points_earned > 0 && <div className="text-[11px] text-teal-700 mt-1 numerals">+{r.points_earned} pts</div>}
                            </div>
                          </div>
                        )) : <div className="text-slate-400 text-[12px] text-center py-3">No referrals submitted yet by this physio.</div>}
                      </div>
                    </>
                  ) : <div className="text-[11px] text-slate-400">Loading…</div>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add physio modal */}
      {open && createPortal(
        <div className="fixed inset-0 bg-slate-900/30 z-40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onSubmit={submit} className="bg-white rounded-xl border border-slate-200 w-full max-w-lg p-6 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="add-physio-modal">
            <div className="flex items-center justify-between"><h3 className="serif-display text-2xl">Onboard physio</h3><button type="button" onClick={() => setOpen(false)}><X size={18} className="text-slate-400" /></button></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="small-caps text-slate-500">First name</label><input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required /></div>
              <div><label className="small-caps text-slate-500">Last name</label><input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required /></div>
              <div className="col-span-2"><label className="small-caps text-slate-500">Mobile</label><input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} required /></div>
              <div><label className="small-caps text-slate-500">City</label><input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
              <div><label className="small-caps text-slate-500">State</label><input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
              <div className="col-span-2"><label className="small-caps text-slate-500">Region</label><input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="e.g. West / South / North / East" data-testid="onb-region" /></div>
              <div className="col-span-2"><label className="small-caps text-slate-500">Clinic</label><input value={form.clinic_name} onChange={(e) => setForm({ ...form, clinic_name: e.target.value })} /></div>
              <div className="col-span-2">
                <label className="small-caps text-slate-500 flex items-center justify-between">Specialty
                  <button type="button" onClick={addSpecialtyInline} className="text-[10.5px] text-teal-700 hover:underline" data-testid="add-specialty-btn">+ Add new</button>
                </label>
                <select value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} data-testid="specialty-select">
                  {specialtyOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="small-caps text-slate-500">ERP Outlet ID</label>
                <input value={form.erp_outlet_id} onChange={(e) => setForm({ ...form, erp_outlet_id: e.target.value.toUpperCase().trim() })} placeholder="e.g. JBPL-MUM-00471" data-testid="onb-erp-outlet-id" required />
              </div>
              <div className="col-span-2">
                <label className="small-caps text-slate-500">Assigned DSE</label>
                <select value={form.assigned_dse} onChange={(e) => setForm({ ...form, assigned_dse: e.target.value })} data-testid="onb-assigned-dse">
                  <option value="">— Select DSE —</option>
                  {dseList.map((d) => <option key={d.id} value={d.id}>{`${d.name || d.email}${d.erp_id ? ' · ' + d.erp_id : ''}`}</option>)}
                </select>
                {dseList.length === 0 && (
                  <div className="text-[11px] text-amber-700 mt-1 flex items-center gap-1" data-testid="onb-no-dse-warning">
                    <AlertCircle size={11} />
                    {dseError
                      ? <>Couldn&apos;t load DSE list — check your network and <button type="button" onClick={fetchAll} className="underline">retry</button>.</>
                      : <>No DSE users in this tenant. Add one in <a href="/users" className="underline">User Management</a> first.</>}
                  </div>
                )}
                {dseList.length > 0 && (
                  <div className="text-[10.5px] text-slate-400 mt-1">{dseList.length} DSE{dseList.length === 1 ? "" : "s"} available</div>
                )}
              </div>
              <div className="col-span-2">
                <label className="small-caps text-slate-500">Preferred Annual Reward</label>
                <input value={form.preferred_annual_reward} onChange={(e) => setForm({ ...form, preferred_annual_reward: e.target.value })} placeholder="e.g. International family vacation" data-testid="onb-preferred-annual" />
              </div>
              <div className="col-span-2">
                <label className="small-caps text-slate-500">Preferred Quarterly Reward</label>
                <input value={form.preferred_quarterly_reward} onChange={(e) => setForm({ ...form, preferred_quarterly_reward: e.target.value })} placeholder="e.g. Smartwatch" data-testid="onb-preferred-quarterly" />
              </div>
            </div>

            {/* DPDP consent block */}
            <div className="surface p-3 space-y-1.5">
              <div className="text-[11.5px] font-medium flex items-center gap-1"><ShieldCheck size={12} className="text-teal-700" /> DPDP Consent (India 2023)</div>
              <label className="flex items-center gap-2 text-[12px]"><input type="checkbox" checked={form.tnc_accepted} onChange={(e) => setForm({ ...form, tnc_accepted: e.target.checked })} data-testid="onb-tnc" /> Accept Terms &amp; Conditions</label>
              <label className="flex items-center gap-2 text-[12px]"><input type="checkbox" checked={form.opt_in_email} onChange={(e) => setForm({ ...form, opt_in_email: e.target.checked })} data-testid="onb-email" /> Email opt-in</label>
              <label className="flex items-center gap-2 text-[12px]"><input type="checkbox" checked={form.opt_in_sms} onChange={(e) => setForm({ ...form, opt_in_sms: e.target.checked })} data-testid="onb-sms" /> SMS opt-in</label>
              <div className="text-[10.5px] text-slate-400 flex items-center gap-1"><Award size={10} className="text-teal-600" /> CBC Champion Certificate will be auto-issued on onboarding.</div>
            </div>

            <button type="submit" className="btn-primary w-full">Onboard</button>
          </form>
        </div>,
        document.body
      )}
    </div>
  );
}
